import React, { useEffect, useState } from 'react';
import { Button, Card, Input, Select, Switch, Tag, message } from 'antd';
import { CarOutlined, ClockCircleOutlined, InboxOutlined, UserOutlined } from '@ant-design/icons';
import { supabase } from '../../utils/supabase';

// Liste des heures entières que le commerçant peut choisir pour les envois quotidiens.
const DAILY_NOTIFICATION_HOURS = Array.from({ length: 24 }, (_, hour) => {
  const value = `${String(hour).padStart(2, '0')}:00`;
  return { value, label: value.replace(':', 'h') };
});

// Pays proposés pour composer automatiquement le numéro WhatsApp au format international.
const PREPARATION_PHONE_COUNTRIES = [
  { value: '+225', label: '🇨🇮 Côte d’Ivoire (+225)' },
  { value: '+221', label: '🇸🇳 Sénégal (+221)' },
  { value: '+223', label: '🇲🇱 Mali (+223)' },
  { value: '+226', label: '🇧🇫 Burkina Faso (+226)' },
  { value: '+228', label: '🇹🇬 Togo (+228)' },
  { value: '+229', label: '🇧🇯 Bénin (+229)' },
  { value: '+237', label: '🇨🇲 Cameroun (+237)' },
  { value: '+243', label: '🇨🇩 RDC (+243)' },
  { value: '+33', label: '🇫🇷 France (+33)' },
  { value: '+1', label: '🇺🇸 États-Unis / Canada (+1)' }
];

const DEFAULT_DELIVERY_LINK_TEMPLATE = 'Bonjour {nom_livreur},\n\nVous avez {nombre_commandes} commande(s) en cours. Consultez votre liste ici :\n{lien_commandes}';
const DEFAULT_PREPARATION_SUMMARY_TEMPLATE = 'Bonjour,\n\nVoici le lien des {nombre_commandes} commande(s) du jour :\n{lien_commandes_du_jour}';

/**
 * Affiche et enregistre les réglages des messages WhatsApp quotidiens destinés
 * aux livreurs et à la préparatrice depuis l'espace de configuration.
 */
const DailyDeliveryNotificationsSettings = () => {
  const [settings, setSettings] = useState({
    daily_delivery_links_enabled: false,
    daily_delivery_links_time: '08:00',
    daily_delivery_links_template: DEFAULT_DELIVERY_LINK_TEMPLATE,
    daily_preparation_summary_enabled: false,
    daily_preparation_summary_phone: '',
    daily_preparation_summary_country_code: '+225',
    daily_preparation_summary_time: '08:00',
    daily_preparation_summary_template: DEFAULT_PREPARATION_SUMMARY_TEMPLATE
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /**
   * Charge les réglages d'envoi quotidien de la boutique active depuis shops.
   * Les valeurs d'heure sont réduites à HH:MM afin d'être compatibles avec les listes du formulaire.
   */
  const loadDailyDeliverySettings = async () => {
    try {
      const shopId = window.localStorage.getItem('activeShopId');
      if (!shopId) throw new Error('Sélectionnez une boutique avant de configurer les envois.');

      const { data, error } = await supabase
        .from('shops')
        .select('storefront_settings')
        .eq('id', shopId)
        .single();

      if (error) throw error;

      const shopSettings = data.storefront_settings || {};
      const phoneCountryCode = shopSettings.daily_preparation_summary_country_code || '+225';
      const localPhone = removeCountryCode(shopSettings.daily_preparation_summary_phone || '', phoneCountryCode);

      setSettings({
        daily_delivery_links_enabled: Boolean(shopSettings.daily_delivery_links_enabled),
        daily_delivery_links_time: shopSettings.daily_delivery_links_time?.slice(0, 5) || '08:00',
        daily_delivery_links_template: shopSettings.daily_delivery_links_template || DEFAULT_DELIVERY_LINK_TEMPLATE,
        daily_preparation_summary_enabled: Boolean(shopSettings.daily_preparation_summary_enabled),
        daily_preparation_summary_phone: localPhone,
        daily_preparation_summary_country_code: phoneCountryCode,
        daily_preparation_summary_time: shopSettings.daily_preparation_summary_time?.slice(0, 5) || '08:00',
        daily_preparation_summary_template: shopSettings.daily_preparation_summary_template || DEFAULT_PREPARATION_SUMMARY_TEMPLATE
      });
    } catch (error) {
      console.error('Erreur chargement réglages quotidiens livraison :', error);
      message.error('Impossible de charger les réglages de livraison');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Charge une seule fois les réglages au montage du composant afin d'afficher
   * les choix actuellement enregistrés pour le commerçant connecté.
   */
  useEffect(() => {
    loadDailyDeliverySettings();
  }, []);

  /**
   * Met à jour localement un réglage simple avant son enregistrement explicite.
   * @param {string} key - Nom de la clé de réglage propre à la boutique.
   * @param {string|boolean} value - Nouvelle valeur saisie par le commerçant.
   */
  const updateSetting = (key, value) => {
    setSettings((previousSettings) => ({ ...previousSettings, [key]: value }));
  };

  /**
   * Enregistre les deux automatisations quotidiennes et vérifie qu'un numéro est
   * renseigné lorsque l'envoi du récapitulatif à la préparatrice est activé.
   */
  const saveDailyDeliverySettings = async () => {
    if (settings.daily_preparation_summary_enabled && !settings.daily_preparation_summary_phone.trim()) {
      message.error('Indiquez le numéro WhatsApp de la préparatrice');
      return;
    }

    try {
      setSaving(true);
      const shopId = window.localStorage.getItem('activeShopId');
      if (!shopId) throw new Error('Sélectionnez une boutique avant de configurer les envois.');

      const { data: shop, error: readError } = await supabase
        .from('shops')
        .select('storefront_settings')
        .eq('id', shopId)
        .single();
      if (readError) throw readError;

      const { error } = await supabase
        .from('shops')
        .update({ storefront_settings: {
          ...(shop.storefront_settings || {}),
          daily_delivery_links_enabled: settings.daily_delivery_links_enabled,
          daily_delivery_links_time: settings.daily_delivery_links_time,
          daily_delivery_links_template: settings.daily_delivery_links_template.trim() || null,
          daily_preparation_summary_enabled: settings.daily_preparation_summary_enabled,
          daily_preparation_summary_phone: buildInternationalPhone(
            settings.daily_preparation_summary_country_code,
            settings.daily_preparation_summary_phone
          ) || null,
          daily_preparation_summary_country_code: settings.daily_preparation_summary_country_code,
          daily_preparation_summary_time: settings.daily_preparation_summary_time,
          daily_preparation_summary_template: settings.daily_preparation_summary_template.trim() || null
        }})
        .eq('id', shopId);

      if (error) throw error;
      message.success('Réglages des envois quotidiens enregistrés');
    } catch (error) {
      console.error('Erreur sauvegarde réglages quotidiens livraison :', error);
      message.error('Impossible d’enregistrer les réglages de livraison');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      loading={loading}
      className="overflow-hidden"
      title={
        <div className="flex items-center gap-2">
          <CarOutlined className="text-primary-600" />
          <span>Récapitulatifs quotidiens</span>
        </div>
      }
      extra={<Tag color="blue" className="private-tag-accent" >Heure d’Abidjan</Tag>}
    >
      <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        Les messages sont envoyés uniquement lorsqu’il existe au moins une commande en cours.
      </div>

      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 transition-colors hover:border-primary-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <CarOutlined />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Lien des commandes aux livreurs</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Chaque livreur ayant des commandes en cours reçoit son lien personnel.
                </p>
              </div>
            </div>
            <Switch
              checked={settings.daily_delivery_links_enabled}
              onChange={(value) => updateSetting('daily_delivery_links_enabled', value)}
            />
          </div>

          <label className="mt-5 flex items-center gap-2 text-sm font-medium text-gray-700" htmlFor="daily-delivery-links-time">
            <ClockCircleOutlined /> Heure d’envoi
          </label>
          <Select
            id="daily-delivery-links-time"
            className="mt-1 w-full"
            options={DAILY_NOTIFICATION_HOURS}
            value={settings.daily_delivery_links_time}
            disabled={!settings.daily_delivery_links_enabled}
            onChange={(value) => updateSetting('daily_delivery_links_time', value)}
          />

          <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="daily-delivery-links-template">
            Message envoyé au livreur
          </label>
          <Input.TextArea
            id="daily-delivery-links-template"
            className="mt-1"
            rows={5}
            value={settings.daily_delivery_links_template}
            disabled={!settings.daily_delivery_links_enabled}
            onChange={(event) => updateSetting('daily_delivery_links_template', event.target.value)}
          />
          <p className="mt-2 text-xs text-gray-500">
            Variables : <code>{'{nom_livreur}'}</code>, <code>{'{nombre_commandes}'}</code>, <code>{'{lien_commandes}'}</code>
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 transition-colors hover:border-primary-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                <InboxOutlined />
              </div>
              <div>
              <h3 className="text-base font-semibold text-gray-900">Liste des commandes à la préparatrice</h3>
              <p className="mt-1 text-sm text-gray-500">
                  Le numéro configuré reçoit le lien public des commandes du jour.
              </p>
              </div>
            </div>
            <Switch
              checked={settings.daily_preparation_summary_enabled}
              onChange={(value) => updateSetting('daily_preparation_summary_enabled', value)}
            />
          </div>

          <label className="mt-5 flex items-center gap-2 text-sm font-medium text-gray-700" htmlFor="daily-preparation-phone">
            <UserOutlined /> Numéro WhatsApp de la préparatrice
          </label>
          <Input
            id="daily-preparation-phone"
            className="mt-1"
            addonBefore={
              <Select
                aria-label="Pays de la préparatrice"
                className="w-48"
                options={PREPARATION_PHONE_COUNTRIES}
                value={settings.daily_preparation_summary_country_code}
                disabled={!settings.daily_preparation_summary_enabled}
                onChange={(value) => updateSetting('daily_preparation_summary_country_code', value)}
              />
            }
            value={settings.daily_preparation_summary_phone}
            disabled={!settings.daily_preparation_summary_enabled}
            placeholder="Ex. 0700000000"
            onChange={(event) => updateSetting('daily_preparation_summary_phone', event.target.value.replace(/[^0-9]/g, ''))}
          />
          <p className="mt-2 text-xs text-gray-500">
            Saisissez seulement le numéro local. L’indicatif du pays sélectionné sera ajouté automatiquement.
          </p>

          <label className="mt-4 flex items-center gap-2 text-sm font-medium text-gray-700" htmlFor="daily-preparation-time">
            <ClockCircleOutlined /> Heure d’envoi
          </label>
          <Select
            id="daily-preparation-time"
            className="mt-1 w-full"
            options={DAILY_NOTIFICATION_HOURS}
            value={settings.daily_preparation_summary_time}
            disabled={!settings.daily_preparation_summary_enabled}
            onChange={(value) => updateSetting('daily_preparation_summary_time', value)}
          />

          <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="daily-preparation-template">
            Message envoyé à la préparatrice
          </label>
          <Input.TextArea
            id="daily-preparation-template"
            className="mt-1"
            rows={5}
            value={settings.daily_preparation_summary_template}
            disabled={!settings.daily_preparation_summary_enabled}
            onChange={(event) => updateSetting('daily_preparation_summary_template', event.target.value)}
          />
          <p className="mt-2 text-xs text-gray-500">
            Variables : <code>{'{nombre_commandes}'}</code>, <code>{'{lien_commandes_du_jour}'}</code>
          </p>
        </div>

        <div className="flex justify-end">
          <Button type="primary" size="large" loading={saving} onClick={saveDailyDeliverySettings}>
            Enregistrer
          </Button>
        </div>
      </div>
    </Card>
  );
};

/**
 * Retire l'indicatif sélectionné d'un numéro existant pour afficher seulement
 * la partie locale dans le champ de saisie après le chargement des réglages.
 * @param {string} phone - Numéro éventuellement déjà enregistré au format international.
 * @param {string} countryCode - Indicatif choisi dans le sélecteur de pays.
 * @returns {string} Numéro local dépourvu de caractères non numériques.
 */
function removeCountryCode(phone, countryCode) {
  const digits = phone.replace(/\D/g, '');
  const countryDigits = countryCode.replace(/\D/g, '');
  return digits.startsWith(countryDigits) ? digits.slice(countryDigits.length) : digits;
}

/**
 * Assemble l'indicatif pays et le numéro local en un numéro international
 * unique, adapté à l'envoi WhatsApp par le serveur.
 * @param {string} countryCode - Indicatif sélectionné, par exemple +225.
 * @param {string} localPhone - Numéro saisi sans indicatif par l'utilisateur.
 * @returns {string} Numéro complet au format +<indicatif><numéro> ou une chaîne vide.
 */
function buildInternationalPhone(countryCode, localPhone) {
  const localDigits = localPhone.replace(/\D/g, '');
  const countryDigits = countryCode.replace(/\D/g, '');
  return localDigits ? `+${countryDigits}${localDigits}` : '';
}

export default DailyDeliveryNotificationsSettings;
